import { memo } from "react";
import { BookingButton } from "@/components/BookingButton";
import { pressQuote, rating } from "@/data/social-proof";
import type { SectionRow, CategoryRow } from "@/lib/categories.functions";
import SectionRenderer from "./sections/SectionRenderer";
import { MediaLayer } from "./sections/MediaLayer";

type Props = {
  category: CategoryRow;
  sections: SectionRow[];
  appendCta?: boolean;
};

function CategorySectionsPageImpl({ category, sections, appendCta }: Props) {
  const hasExplicitHero = sections.some((s) => s.type === "hero");
  return (
    <div className="relative w-full">
      {!hasExplicitHero && (category.hero_media_url || sections.length === 0) && (
        <ImplicitHero category={category} />
      )}
      {sections.map((s) => (
        <SectionRenderer key={s.id} section={s} />
      ))}
      {appendCta && <CtaSection />}
    </div>
  );
}

const CategorySectionsPage = memo(CategorySectionsPageImpl);
export default CategorySectionsPage;

function ImplicitHero({ category }: { category: CategoryRow }) {
  const media =
    category.hero_media_url && category.hero_media_type
      ? { url: category.hero_media_url, type: category.hero_media_type }
      : undefined;
  return (
    <section className="mobile-hero relative w-full overflow-hidden vignette aspect-[4/5] sm:aspect-[16/10] max-h-[78svh]">
      <MediaLayer media={media} alt={category.label} priority />
      <div className="mobile-hero-scrim" />
      <div className="mobile-hero-copy px-7 flex flex-col">
        <div className="mobile-hero-kicker text-[10px] tracking-[0.4em] uppercase mb-3 text-shadow-soft">
          {category.label}
        </div>
        <h2 className="mobile-hero-title font-serif text-4xl sm:text-5xl leading-[1.05] text-shadow-soft max-w-[16ch]">
          {category.label}
        </h2>
        <p className="mobile-hero-subtitle mt-3 text-sm sm:text-base font-light max-w-[42ch] text-shadow-soft">
          Content coming soon.
        </p>
      </div>
    </section>
  );
}

function CtaSection() {
  return (
    <section
      className="relative w-full overflow-hidden bg-background"
      style={{ minHeight: "92svh" }}
    >
      <div className="absolute inset-0 flex flex-col items-center justify-center px-8 text-center">
        <div className="text-[10px] tracking-[0.45em] uppercase text-gold/90 mb-6">
          Time together feels different here
        </div>
        <div className="flex items-center gap-3 mb-2">
          <span className="font-serif text-2xl text-foreground">{rating.score.toFixed(1)}</span>
          <span className="text-foreground/55 text-sm">/ 5</span>
        </div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-foreground/55 mb-10">
          From {rating.count}+ {rating.source.toLowerCase()}
        </div>
        <blockquote className="font-serif italic text-xl md:text-2xl leading-snug text-foreground/95 max-w-[24ch] text-shadow-soft">
          &ldquo;{pressQuote.quote}&rdquo;
        </blockquote>
        <div className="mt-4 text-[10px] tracking-[0.3em] uppercase text-gold/85">
          — {pressQuote.outlet}
        </div>
        <BookingButton className="mt-12 font-serif italic text-base text-background bg-gold/95 px-8 py-3.5 rounded-full tracking-wide shadow-[0_8px_40px_-12px_rgba(0,0,0,0.8)] hover:bg-gold transition">
          Book your stay
        </BookingButton>
      </div>
    </section>
  );
}
